import React, { useState, useCallback } from 'react';
import { inject, observer } from 'mobx-react';
import { Grid, Tabs } from '@mantine/core';
import { SimulationContextProvider } from '../../../global/hooks/useSimulation.tsx';
import MMECalculationsSection from '../Widget/MMECalculationsSection.tsx';
import DosageModelingSimulation from '../Widget/DosageModelingSimulation.tsx';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { OTPParametersSection } from '../Widget/OTPParametersSection.tsx';
import { OTPHistorySection } from '../Widget/OTPHistorySection.tsx';
import { useTranslation } from 'react-i18next';

const TDAH_THERAPEUTIC_INDICATION_ID = 1;
const PAIN_THERAPEUTIC_INDICATION_IDS: number[] = [4, 5, 7, 8];

interface IDosageModelingProps {
    patientStore?: PatientStore;
}

const DosageModeling: React.FC<IDosageModelingProps> = inject('patientStore')(
    observer(({ patientStore }) => {
        const { t } = useTranslation();
        const therapeuticIndicationId = patientStore?.therapeuticIndicationId
            ? Number(patientStore.therapeuticIndicationId)
            : undefined;

        const showDosageModeling = therapeuticIndicationId === TDAH_THERAPEUTIC_INDICATION_ID;
        const showMmeCalculations =
            therapeuticIndicationId !== undefined && PAIN_THERAPEUTIC_INDICATION_IDS.includes(therapeuticIndicationId);

        const [activeTab, setActiveTab] = useState<string | null>('otp-parameters');

        const navigateToMMETab = useCallback(() => {
            setActiveTab('mme-calculations');
        }, []);

        return (
            <Grid gutter="lg">
                {(showDosageModeling) && (
                    <Grid.Col span={12}>
                        <DosageModelingSimulation />
                    </Grid.Col>
                )}

                {(showMmeCalculations) && (
                    <Grid.Col span={12}>
                        <Tabs value={activeTab} onChange={setActiveTab}>
                            <Tabs.List>
                                <Tabs.Tab value="otp-parameters">
                                    {t('dosageModeling.tabs.otpParameters')}
                                </Tabs.Tab>
                                <Tabs.Tab value="otp-history">
                                    {t('dosageModeling.tabs.otpHistory')}
                                </Tabs.Tab>
                                <Tabs.Tab value="mme-calculations">
                                    {t('dosageModeling.tabs.mmeCalculations')}
                                </Tabs.Tab>
                            </Tabs.List>
                            <Tabs.Panel value="otp-parameters" pt="md" keepMounted>
                                <OTPParametersSection onNavigateToMMETab={navigateToMMETab} />
                            </Tabs.Panel>
                            <Tabs.Panel value="otp-history" pt="md">
                                <OTPHistorySection />
                            </Tabs.Panel>
                            <Tabs.Panel value="mme-calculations" pt="md">
                                <MMECalculationsSection />
                            </Tabs.Panel>
                        </Tabs>
                    </Grid.Col>
                )}
            </Grid>
        );
    }),
);

const WrappedDosageModeling: React.FC = () => {
    return (
        <SimulationContextProvider>
            <DosageModeling />
        </SimulationContextProvider>
    );
};

export default WrappedDosageModeling;
